/**
 * Widget Theme Hook
 * 
 * Provides CSS variable-based theming for widget components.
 * Uses --dbfox-widget-* CSS variables with hardcoded fallbacks.
 * 
 * Pod apps can customize appearance by setting CSS variables:
 * 
 * :root {
 *   --dbfox-widget-bg: #1a1a2e;
 *   --dbfox-widget-text: #ffffff;
 *   --dbfox-widget-primary: #e94560;
 * }
 */

import { WidgetTheme, WidgetThemeColors, WidgetThemeRadius } from './types';

/**
 * Helper to create a CSS variable reference with fallback
 */
const cssVar = (name: string, fallback: string): string => 
  `var(--dbfox-widget-${name}, ${fallback})`;

/**
 * Default theme using CSS variables with fallbacks
 * These fallbacks match the widget's current color scheme
 */
export const defaultWidgetTheme: WidgetTheme = {
  colors: {
    bg: cssVar('bg', '#ffffff'),
    bgAlt: cssVar('bg-alt', '#f8fafc'),
    text: cssVar('text', '#1e293b'),
    textMuted: cssVar('text-muted', '#475569'),
    textTertiary: cssVar('text-tertiary', '#64748b'),
    border: cssVar('border', '#e2e8f0'),
    primary: cssVar('primary', '#3182ce'),
    primaryHover: cssVar('primary-hover', '#2c5282'),
    primaryLight: cssVar('primary-light', '#dbeafe'),
    success: cssVar('success', '#16a34a'),
    successBg: cssVar('success-bg', '#dcfce7'),
    error: cssVar('error', '#dc2626'),
    errorBg: cssVar('error-bg', '#fee2e2'),
    warning: cssVar('warning', '#d97706'),
    infoBg: cssVar('info-bg', '#dbeafe'),
  },
  radius: {
    sm: cssVar('radius-sm', '6px'),
    md: cssVar('radius', '8px'),
    lg: cssVar('radius-lg', '12px'),
  },
  fontSans: cssVar('font-sans', "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"),
  fontMono: cssVar('font-mono', "'Courier New', Courier, monospace"),
};

/**
 * Hook to get theme values for widget components
 * 
 * @param overrides - Optional partial theme to merge with defaults
 * @returns Complete theme object with CSS variable references
 * 
 * @example
 * const theme = useWidgetTheme();
 * <div style={{ backgroundColor: theme.colors.bgAlt }}>
 */
export function useWidgetTheme(overrides?: Partial<WidgetTheme>): WidgetTheme {
  if (!overrides) {
    return defaultWidgetTheme;
  }
  
  return {
    colors: { ...defaultWidgetTheme.colors, ...overrides.colors },
    radius: { ...defaultWidgetTheme.radius, ...overrides.radius },
    fontSans: overrides.fontSans || defaultWidgetTheme.fontSans,
    fontMono: overrides.fontMono || defaultWidgetTheme.fontMono,
  };
}

/**
 * For main app usage - maps --v2-* CSS variables to theme object
 * This allows Core components to work in the main app context too
 */
export const mainAppTheme: WidgetTheme = {
  colors: {
    bg: 'var(--v2-surface)',
    bgAlt: 'var(--v2-surface-alt)',
    text: 'var(--v2-text)',
    textMuted: 'var(--v2-text-muted)',
    textTertiary: 'var(--v2-text-tertiary)',
    border: 'var(--v2-border)',
    primary: 'var(--v2-primary)',
    primaryHover: 'var(--v2-primary-hover)',
    primaryLight: 'var(--v2-primary-light)',
    success: 'var(--v2-success)',
    successBg: 'var(--v2-success-bg)',
    error: 'var(--v2-error)',
    errorBg: 'var(--v2-error-bg)',
    warning: 'var(--v2-warning)',
    infoBg: 'var(--v2-info-bg)',
  },
  radius: {
    sm: 'var(--radius-sm)',
    md: 'var(--radius-md)',
    lg: 'var(--radius-lg)',
  },
  fontSans: 'var(--font-sans)',
  fontMono: 'var(--font-mono)',
};

export default useWidgetTheme;
